import * as React from 'react'
import { DayPicker } from 'react-day-picker'

import { cn } from '@/shared/lib/styles/class-merge'
import { buttonVariants } from '@/shared/ui/button'
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons'
import { es } from 'date-fns/locale'

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
    fillSpace?: boolean
}

function Calendar({ className, classNames, showOutsideDays = true, fillSpace = false, ...props }: CalendarProps) {
    const { locale } = props
    return (
        <DayPicker
            showOutsideDays={showOutsideDays}
            className={cn('p-3', className)}
            classNames={{
                months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
                month: fillSpace ? 'w-full space-y-4' : 'space-y-4',
                caption: 'flex justify-center pt-1 relative items-center',
                caption_label: 'text-sm font-medium',
                nav: 'space-x-1 flex items-center',
                nav_button: cn(
                    buttonVariants({ variant: 'outline' }),
                    'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
                ),
                nav_button_previous: 'absolute left-1',
                nav_button_next: 'absolute right-1',
                table: 'w-full border-collapse space-y-1',
                head_row: fillSpace ? 'flex' : 'grid grid-cols-7',
                head_cell: cn('text-muted-foreground rounded-md font-normal text-[0.8rem]', fillSpace && 'w-full'),
                row: fillSpace ? 'flex w-full gap-0.5 mt-2' : 'grid grid-cols-7 mt-2',
                cell: cn(
                    'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md',
                    props.mode === 'range'
                        ? '[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md'
                        : '[&:has([aria-selected])]:rounded-md',
                    fillSpace && 'w-full',
                ),
                day: cn(
                    buttonVariants({ variant: 'ghost' }),
                    fillSpace
                        ? 'h-full w-full font-bold aspect-square lg:text-lg'
                        : 'h-8 w-8 font-normal text-sm mx-auto',
                    'p-0 aria-selected:opacity-100',
                ),
                day_range_start: 'day-range-start',
                day_range_end: 'day-range-end',
                day_selected:
                    'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
                day_today: 'bg-accent text-accent-foreground',
                day_outside:
                    'day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground',
                day_disabled: 'text-muted-foreground opacity-50',
                day_range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
                day_hidden: 'invisible',
                ...classNames,
            }}
            components={{
                IconLeft: ({ className, ...props }) => (
                    <ChevronLeftIcon className={cn('h-4 w-4', className)} {...props} />
                ),
                IconRight: ({ className, ...props }) => (
                    <ChevronRightIcon className={cn('h-4 w-4', className)} {...props} />
                ),
            }}
            locale={locale ? locale : es}
            {...props}
        />
    )
}
Calendar.displayName = 'Calendar'

export { Calendar }
